import Vue from "vue";
import Vuex from "vuex";
import router from "./router";
import axios from "axios";

const ensureSubmenu = (menuBlock, name, route) => {
    // Check both submenus and subMenu for compatibility
    const subKey = menuBlock.submenus ? "submenus" : menuBlock.subMenu ? "subMenu" : "submenus";
    if (!menuBlock[subKey]) menuBlock[subKey] = [];
    
    const exists = menuBlock[subKey].some((submenu) => submenu && submenu.route === route);
    if (!exists) {
        menuBlock[subKey].push({ name, route });
    }
    // Sync both keys to ensure all layouts work
    menuBlock.submenus = menuBlock[subKey];
    menuBlock.subMenu = menuBlock[subKey];
};

const isAdmin = (state) => {
    const positionId = state.positionId || localStorage.getItem("positionId");
    return positionId === "62a2de91ecd959516cd3b0e6";
};

// Read-only executive (CEO) position. Kept in sync with backend src/config/roles.js
const CEO_POSITION_ID = "670000000000000000000ce0";
const isCeo = (state) => {
    const positionId = state.positionId || localStorage.getItem("positionId");
    return positionId === CEO_POSITION_ID;
};

const ensureLateArrivalMenus = (menus, state) => {
    if (!Array.isArray(menus)) return [];

    const normalizedMenus = menus.map((menuItem) => ({
        ...menuItem,
        submenus: Array.isArray(menuItem.submenus) ? [...menuItem.submenus] : (Array.isArray(menuItem.subMenu) ? [...menuItem.subMenu] : []),
    }));

    // CEO is a read-only role: skip all the employee/admin menu injection below
    // and show only a single "Dashboard" entry pointing at the overview.
    if (isCeo(state)) {
        return [{
            _id: "ceo_dashboard_section",
            name: "Dashboard",
            menu: { name: "Dashboard", icon: "mdi-view-dashboard" },
            submenus: [{ name: "Overview", route: "/" }],
            subMenu: [{ name: "Overview", route: "/" }],
        }];
    }

    const attendanceMenu = normalizedMenus.find(
        (menuItem) => {
            if (!menuItem) return false;
            const menuName = (menuItem.menu && typeof menuItem.menu.name === "string") ? menuItem.menu.name.toLowerCase() : "";
            const itemName = (typeof menuItem.name === "string") ? menuItem.name.toLowerCase() : "";
            return (
                menuName === "attendance" || menuName === "time management" ||
                itemName === "attendance" || itemName === "time management"
            );
        }
    );

    if (attendanceMenu) {
        // Visible to all users with Attendance menu
        ensureSubmenu(attendanceMenu, "Leave Balance", "/Attendance/LeaveBalance");
        ensureSubmenu(attendanceMenu, "Attendance penalty", "/Attendance/lateArrivalPenalty");
        ensureSubmenu(attendanceMenu, "On Forest Duty", "/Attendance/myOnforestduty");
        
        if (!isAdmin(state)) {
            ensureSubmenu(attendanceMenu, "My Out of Office", "/Attendance/myOutofOffice");
        }

        // Admin only configuration menus
        if (isAdmin(state)) {
            ensureSubmenu(attendanceMenu, "Reports", "/Attendance/reportList");
            ensureSubmenu(attendanceMenu, "Add to Exclude List", "/Attendance/lateArrivalPenaltyExcludeAdd");
            ensureSubmenu(attendanceMenu, "Excluded List", "/Attendance/lateArrivalPenaltyExcludeList");
            ensureSubmenu(attendanceMenu, "On Forest Duty Requests", "/Attendance/onforestdutyRequests");
            ensureSubmenu(attendanceMenu, "Out of Office Requests", "/Attendance/outofofficeRequests");
        }
    } else {
        const timeSubmenus = [];
        if (!isAdmin(state)) {
            timeSubmenus.push({ name: "My Attendance", route: "/Attendance/teamMemberAttendance" });
            timeSubmenus.push({ name: "Apply Leave", route: "/Attendance/leaveForm" });
            timeSubmenus.push({ name: "My Leaves", route: "/Attendance/myLeaves" });
            timeSubmenus.push({ name: "Travel Request", route: "/Attendance/travelRequestForm" });
            timeSubmenus.push({ name: "Out of Office Request", route: "/Attendance/outofofficeRequestForm" });
            timeSubmenus.push({ name: "My Out of Office", route: "/Attendance/myOutofOffice" });
            timeSubmenus.push({ name: "On Forest Duty", route: "/Attendance/myOnforestduty" });
            timeSubmenus.push({ name: "My Travelogue", route: "/Attendance/myTravelogue" });
        } else {
            timeSubmenus.push({ name: "Team Attendance", route: "/Attendance/teamAttendance" });
            timeSubmenus.push({ name: "Leave Requests", route: "/Attendance/leaveRequests" });
            timeSubmenus.push({ name: "Travel Requests", route: "/Attendance/travelRequests" });
            timeSubmenus.push({ name: "Out of Office Requests", route: "/Attendance/outofofficeRequests" });
            timeSubmenus.push({ name: "On Forest Duty Requests", route: "/Attendance/onforestdutyRequests" });
            timeSubmenus.push({ name: "Reports", route: "/Attendance/reportList" });
        }
        timeSubmenus.push({ name: "Leave Balance", route: "/Attendance/LeaveBalance" });
        timeSubmenus.push({ name: "Attendance penalty", route: "/Attendance/lateArrivalPenalty" });
        if (isAdmin(state)) {
            timeSubmenus.push({ name: "Add to Exclude List", route: "/Attendance/lateArrivalPenaltyExcludeAdd" });
            timeSubmenus.push({ name: "Excluded List", route: "/Attendance/lateArrivalPenaltyExcludeList" });
        }
        normalizedMenus.push({
            _id: "time_management_section",
            name: "Time Management",
            menu: { name: "Time Management" },
            submenus: timeSubmenus,
            subMenu: timeSubmenus
        });
    }

    const excelMenu = normalizedMenus.find(
        (menuItem) =>
            menuItem &&
            menuItem.menu &&
            typeof menuItem.menu.name === "string" &&
            menuItem.menu.name.toLowerCase() === "excel reports"
    );

    if (excelMenu) {
        ensureSubmenu(excelMenu, "Attendance penalty", "/ExcelReports/lateArrivalPenalty");
    }

    // Targets (Visible to all employees once menus are loaded, with My Target excluded for Admin)
    const targetSubmenus = [];

    if (!isAdmin(state)) {
        targetSubmenus.push({ name: "My Target", route: "/Targets/myTarget" });
    }

    targetSubmenus.push({ name: "Team Targets", route: "/Targets/teamMembers" });

    if (isAdmin(state)) {
        targetSubmenus.push({ name: "Add Target", route: "/Targets/addTarget" });
        targetSubmenus.push({ name: "Target Logs", route: "/Targets/TargetLogs" });
    }

    const targetMenu = normalizedMenus.find(
        (menuItem) =>
            menuItem &&
            menuItem.menu &&
            typeof menuItem.menu.name === "string" &&
            ["target", "targets"].includes(menuItem.menu.name.toLowerCase())
    );

    if (targetMenu) {
        // Remove existing "Logs" and "Target logs" that might come from the database
        const subKey = targetMenu.submenus ? "submenus" : targetMenu.subMenu ? "subMenu" : "submenus";
        if (targetMenu[subKey]) {
            targetMenu[subKey] = targetMenu[subKey].filter(
                (sub) => {
                    if (!sub.name) return false;
                    const nameLower = sub.name.toLowerCase();
                    if (["logs", "target logs"].includes(nameLower)) return false;
                    if (isAdmin(state) && ["my target", "my targets"].includes(nameLower)) return false;
                    return true;
                }
            );
            targetMenu.submenus = targetMenu[subKey];
            targetMenu.subMenu = targetMenu[subKey];
        }

        targetSubmenus.forEach((sub) => {
            ensureSubmenu(targetMenu, sub.name, sub.route);
        });
    } else {
        normalizedMenus.push({
            _id: "targets_section",
            name: "Targets",
            menu: { name: "Targets" },
            submenus: targetSubmenus,
            subMenu: targetSubmenus
        });
    }

    // Troubleshoot / Report Issue (Visible to all)
    const troubleshootMenu = normalizedMenus.find(
        (menuItem) =>
            menuItem &&
            (menuItem.name === "Troubleshoot" || (menuItem.menu && menuItem.menu.name === "Troubleshoot"))
    );

    const troubleshootSubmenus = [
        { name: "Report Issue", route: "/Troubleshoot/Report" },
        { name: "My Tickets", route: "/Troubleshoot/MyTickets" }
    ];

    if (isAdmin(state)) {
        troubleshootSubmenus.push({ name: "Manage Tickets", route: "/Troubleshoot/Admin/Manage" });
    }

    if (troubleshootMenu) {
        troubleshootSubmenus.forEach(sub => {
            ensureSubmenu(troubleshootMenu, sub.name, sub.route);
        });
    } else {
        normalizedMenus.push({
            _id: "troubleshoot_section",
            name: "Troubleshoot",
            menu: { name: "Troubleshoot" },
            submenus: troubleshootSubmenus,
            subMenu: troubleshootSubmenus
        });
    }

    // Payslips (Visible to all, Upload for Admin)
    const payslipMenu = normalizedMenus.find(
        (menuItem) =>
            menuItem &&
            (menuItem.name === "Payslips" || (menuItem.menu && menuItem.menu.name === "Payslips"))
    );

    const payslipSubmenus = [];

    if (!isAdmin(state)) {
        payslipSubmenus.push({ name: "My Payslips", route: "/Payslips/MyPayslips" });
    } else {
        payslipSubmenus.push({ name: "Upload Payslips", route: "/Payslips/Upload" });
    }

    if (payslipMenu) {
        payslipSubmenus.forEach(sub => {
            ensureSubmenu(payslipMenu, sub.name, sub.route);
        });
    } else {
        normalizedMenus.push({
            _id: "payslips_section",
            name: "Payslips",
            menu: { name: "Payslips" },
            submenus: payslipSubmenus,
            subMenu: payslipSubmenus
        });
    }

    return normalizedMenus;
};

Vue.use(Vuex);
export default new Vuex.Store({
    state: {
        isLoggedIn: !!localStorage.getItem("token"),
        userType: localStorage.getItem("userType"),
        role: localStorage.getItem("userType"),
        positionId: localStorage.getItem("positionId") && localStorage.getItem("positionId") !== "undefined" && localStorage.getItem("positionId") !== "null" ? localStorage.getItem("positionId") : null,

        userData: {},
        fullData: {},
        email: "",
        menus: ensureLateArrivalMenus(JSON.parse(localStorage.getItem("menus") || "[]"), {
            userType: localStorage.getItem("userType"),
            role: localStorage.getItem("userType"),
            userData: {}
        }),
        initial: "",
        appLoading: false,
        exceptPos: {},
        currentPage: null,
        date: "",
        status: "",
        travelMode: "",
        assignee: [],
    },
    mutations: {
        loginUser(state, payload) {
            localStorage.setItem("token", payload);
            state.isLoggedIn = true;
            // if (state.userType == 'Admin') router.push('/admin')
            // else router.push('/dashboard')

            router.push("/");
        },
        logoutUser(state) {
            state.userType = null;
            state.isLoggedIn = false;
            state.userData = {};
            state.initial = "";

            axios({
                method: "GET",
                url: "/employee/logout",
                headers: {
                    "x-auth-token": localStorage.getItem("token")
                }
            }).then(response => {
                if (response.data) {
                    state.userType = null
                    state.isLoggedIn = false
                    state.userData = {}
                    state.initial = "";
                    localStorage.removeItem("userType");
                    localStorage.removeItem("userid");
                    localStorage.removeItem('departmentId')
                    localStorage.removeItem("token");
                    localStorage.removeItem("positionId")
                    router.push("/login");
                }
            })
        },
        menu(state, item) {
            state.menus = ensureLateArrivalMenus(item, state);
            localStorage.setItem("menus", JSON.stringify(state.menus));
            if (state.menus.length > 0) {
                if (state.menus[0]._id !== "60ed5bd072b4ca543c57424d") {
                    if (state.menus[0].submenus.length > 0) {
                        if (state.menus.length > 0) state.initial = state.menus[0].submenus[0].route;
                        else state.initial = "/";
                    } else {
                        state.initial = "/";
                    }
                } else {
                    state.initial = "/";
                }
            } else {
                state.initial = "/";
            }
        },
        userData(state, payload) {
            state.userData = payload;
            state.email = state.userData.email;
        },
        fullData(state, payload) {
            state.fullData = payload;
            if (state.fullData.data && state.fullData.data.positionid) {
                state.exceptPos = state.fullData.data.positionid.filter(
                    (x) => x._id != (state.fullData.position || localStorage.getItem("positionId"))
                );
            } else {
                state.exceptPos = [];
            }
            
            if (state.fullData.data && state.fullData.data._id) {
                localStorage.setItem("userid", state.fullData.data._id);
            }

            if (state.fullData.position) {
                localStorage.setItem("positionId", state.fullData.position);
                state.positionId = state.fullData.position;
            }

            if (state.fullData.departmentId) {
                localStorage.setItem('departmentId', state.fullData.departmentId)
                console.log("Current Dept", state.fullData.departmentId);
            }
        },
        assignee(state, payload) {
            state.assignee = payload;
        },
        userType(state, payload) {
            localStorage.setItem("userType", payload);
            state.userType = payload;
        },
        role(state, item) {
            state.role = item
        },
        positionId(state, item) {
            state.positionId = item;
            localStorage.setItem("positionId", item);
        },
        changeDate(state, item) {
            state.date = item
        },
        changeStatus(state, item) {
            state.status = item
        },
        changeCurrentPage(state, item) {
            state.currentPage = item
        },
        changeCurrentPageTeam(state, item) {
            state.currentPage = item
        },
        changeTravel(state, item) {
            state.travelMode = item
        },
        changeTravelStatus(state, item) {
            state.status = item
        },
        changeTravelDate(state, item) {
            state.date = item
        },
        appLoading(state, payload) {
            state.appLoading = payload;
        },
        sessionOut(state) {
            localStorage.removeItem("token");
            state.isLoggedIn = false;
            router.push("/login");
        },
    },
    getters: {
        isAdmin(state) {
            return isAdmin(state);
        },
        isCeo(state) {
            return isCeo(state);
        },
    },
});
